"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AutonomyLimitStopOrders = void 0;
const tslib_1 = require("tslib");
const abi_1 = require("@ethersproject/abi");
const abstract_provider_1 = require("@ethersproject/abstract-provider");
const abstract_signer_1 = require("@ethersproject/abstract-signer");
const address_1 = require("@ethersproject/address");
const constants_1 = require("@ethersproject/constants");
const solidity_1 = require("@ethersproject/solidity");
const sdk_core_1 = require("@uniswap/sdk-core");
const v2_sdk_1 = require("@uniswap/v2-sdk");
const IUniswapV2Pair_json_1 = require("@uniswap/v2-core/build/IUniswapV2Pair.json");
const axios_1 = require("axios");
const ethers_1 = require("ethers");
const jsbi_1 = (0, tslib_1.__importDefault)(require("jsbi"));
const AutonomyMidRouter_json_1 = (0, tslib_1.__importDefault)(require("../abis/AutonomyMidRouter.json"));
const AutonomyRegistry_json_1 = (0, tslib_1.__importDefault)(require("../abis/AutonomyRegistry.json"));
const ERC20_json_1 = (0, tslib_1.__importDefault)(require("../abis/ERC20.json"));
const multicall2_json_1 = (0, tslib_1.__importDefault)(require("../abis/multicall2.json"));
const constants_2 = require("../constants");
const chains_1 = require("../constants/chains");
const tokens_1 = require("../constants/tokens");
const utils_1 = require("../utils");
class AutonomyLimitStopOrders {
    constructor(chainId, signerOrProvider, routerAddress, factoryAddress, initCodeHash) {
        this._chainId = chainId;
        this._routerAddress = routerAddress
            ? routerAddress
            : chainId === chains_1.CHAIN_ID.BSC
                ? constants_2.APESWAP_ROUTER_ADDRESS
                : constants_2.TRADERJOE_ROUTER_ADDRESS;
        this._factoryAddress = factoryAddress
            ? factoryAddress
            : chainId === chains_1.CHAIN_ID.BSC
                ? constants_2.APESWAP_FACTORY_ADDRESS
                : constants_2.TRADERJOE_FACTORY_ADDRESS;
        this._initCodeHash = initCodeHash
            ? initCodeHash
            : chainId === chains_1.CHAIN_ID.BSC
                ? constants_2.APESWAP_INIT_CODE_HASH
                : constants_2.TRADERJOE_INIT_CODE_HASH;
        this._signer = abstract_signer_1.Signer.isSigner(signerOrProvider) ? signerOrProvider : undefined;
        this._provider = abstract_provider_1.Provider.isProvider(signerOrProvider)
            ? signerOrProvider
            : abstract_signer_1.Signer.isSigner(signerOrProvider)
                ? signerOrProvider.provider
                : undefined;
        this._router = new ethers_1.Contract(this._routerAddress, constants_2.ROUTER_ABI[this._chainId], signerOrProvider);
        this._factory = new ethers_1.Contract(this._factoryAddress, constants_2.FACTORY_ABI[this._chainId], signerOrProvider);
        this._autonomyRegistry = new ethers_1.Contract(constants_2.AUTONOMY_REGISTRY_ADDRESS[this._chainId], AutonomyRegistry_json_1.default, signerOrProvider);
        this._autonomyMidRouter = new ethers_1.Contract(constants_2.AUTONOMY_MIDROUTER_ADDRESS[this._chainId], AutonomyMidRouter_json_1.default, signerOrProvider);
        this._multicall = new ethers_1.Contract(constants_2.MULTICALL_ADDRESS[this._chainId], multicall2_json_1.default, signerOrProvider);
        this._api = new axios_1.Axios({
            baseURL: "https://q4oh5828wc.execute-api.us-east-1.amazonaws.com",
        });
    }
    get chainId() {
        return this.chainId;
    }
    get provider() {
        return this._provider;
    }
    get signer() {
        return this._signer;
    }
    getTradePath(inputToken, outputToken, inputAmount) {
        var _a, _b;
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const inputTokenContract = this.getTokenContract(inputToken);
            const inputTokenDecimals = yield inputTokenContract.decimals();
            const outputTokenContract = this.getTokenContract(inputToken);
            const outputTokenDecimals = yield outputTokenContract.decimals();
            const tokenA = new sdk_core_1.Token(this._chainId, inputToken, inputTokenDecimals);
            const tokenB = new sdk_core_1.Token(this._chainId, outputToken, outputTokenDecimals);
            const bases = (_a = tokens_1.COMMON_BASES[this._chainId]) !== null && _a !== void 0 ? _a : [];
            const basePairs = bases.flatMap((base) => bases.map((otherBase) => [base, otherBase]));
            const tokens = [
                [tokenA, tokenB],
                ...bases.map((base) => [tokenA, base]),
                ...bases.map((base) => [tokenB, base]),
                ...basePairs,
            ]
                .filter(([t0, t1]) => !t0.equals(t1))
                .filter(([t0, t1], i, otherPairs) => {
                const firstIndexInOtherPairs = otherPairs.findIndex(([t0Other, t1Other]) => {
                    return (t0.equals(t0Other) && t1.equals(t1Other)) || (t0.equals(t1Other) && t1.equals(t0Other));
                });
                return firstIndexInOtherPairs === i;
            });
            const pairAddresses = tokens.map(([tokenA, tokenB]) => tokenA && tokenB && tokenA.chainId === tokenB.chainId && !tokenA.equals(tokenB)
                ? this.calculatePairAddress({ tokenA, tokenB })
                : undefined);
            const results = yield this.getMultipleContractSingleData(pairAddresses, new abi_1.Interface(IUniswapV2Pair_json_1.abi), "getReserves");
            const allowedPairs = results
                .map((reserves, i) => {
                const tokenA = tokens[i][0];
                const tokenB = tokens[i][1];
                if (!tokenA || !tokenB || tokenA.equals(tokenB))
                    return null;
                if (!reserves) {
                    return null;
                }
                const { reserve0, reserve1 } = reserves;
                const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA];
                return new v2_sdk_1.Pair(sdk_core_1.CurrencyAmount.fromRawAmount(token0, reserve0.toString()), sdk_core_1.CurrencyAmount.fromRawAmount(token1, reserve1.toString()));
            })
                .filter((pair) => pair !== null);
            let bestTradeSoFar = null;
            for (let i = 1; i <= constants_2.MAX_HOPS; i++) {
                const options = { maxHops: i, maxNumResults: 1 };
                const currentTrade = (_b = v2_sdk_1.Trade.bestTradeExactIn(allowedPairs, sdk_core_1.CurrencyAmount.fromRawAmount(tokenA, jsbi_1.default.BigInt(inputAmount)), tokenB, options)[0]) !== null && _b !== void 0 ? _b : null;
                // if current trade is best yet, save it
                if ((0, utils_1.isTradeBetter)(bestTradeSoFar, currentTrade, constants_2.BETTER_TRADE_LESS_HOPS_THRESHOLD)) {
                    bestTradeSoFar = currentTrade;
                }
            }
            return bestTradeSoFar === null || bestTradeSoFar === void 0 ? void 0 : bestTradeSoFar.route.path.map((token) => token.address);
        });
    }
    approve(inputToken, inputAmount, recipient) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const tokenContract = this.getTokenContract(inputToken);
            const allowance = yield tokenContract.allowance(recipient, this._autonomyMidRouter.address);
            if (allowance.lt(inputAmount)) {
                let useExact = false;
                const estimatedGas = yield tokenContract.estimateGas
                    .approve(this._autonomyMidRouter.address, constants_1.MaxUint256)
                    .catch(() => {
                    // general fallback for tokens who restrict approval amounts
                    useExact = true;
                    return tokenContract.estimateGas.approve(this._autonomyMidRouter.address, inputAmount.toString());
                });
                return tokenContract
                    .approve(this._autonomyMidRouter.address, useExact ? inputAmount.toString() : constants_1.MaxUint256, {
                    gasLimit: (0, utils_1.calculateGasMargin)(estimatedGas),
                })
                    .catch((error) => {
                    console.debug("Failed to approve token", error);
                    throw error;
                });
            }
        });
    }
    submitOrder(orderType, inputToken, inputAmount, outputToken, outputAmount, recipient, prepay) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const path = yield this.getTradePath(inputToken, outputToken, inputAmount);
            const deadline = constants_2.TRANSACTION_DEADLINE;
            const swapCalls = this.encodeSwapCall(orderType, inputToken, outputToken, inputAmount, outputAmount, {
                recipient,
                prepay,
                path,
                deadline,
            });
            const estimatedCalls = yield Promise.all(swapCalls.map((call) => {
                const { parameters: { methodName, args, value }, contract, } = call;
                const options = !value || (0, utils_1.isZero)(value) ? {} : { value };
                return contract.estimateGas[methodName](...args, options)
                    .then((gasEstimate) => {
                    return { call, gasEstimate };
                })
                    .catch((gasError) => {
                    console.info("Gas estimate failed, trying eth_call to extract error", call);
                    return contract.callStatic[methodName](...args, options)
                        .then((result) => {
                        console.info("Unexpected successful call after failed estimate gas", call, gasError, result);
                        return {
                            call,
                            error: new Error("Unexpected issue with estimating the gas. Please try again."),
                        };
                    })
                        .catch((callError) => {
                        var _a;
                        console.info("Call threw error", call, callError);
                        let errorMessage;
                        switch (callError.reason) {
                            case "UniswapV2Router: INSUFFICIENT_OUTPUT_AMOUNT":
                            case "UniswapV2Router: EXCESSIVE_INPUT_AMOUNT":
                                errorMessage =
                                    "This transaction will not succeed either due to price movement or fee on transfer. Try increasing your slippage tolerance.";
                                break;
                            default:
                                errorMessage = `The transaction cannot succeed due to error: ${callError.reason || ((_a = callError.data) === null || _a === void 0 ? void 0 : _a.message) || callError.message}. This is probably an issue with one of the tokens you are swapping.`;
                        }
                        return { call, error: new Error(errorMessage) };
                    });
                });
            }));
            const successfulEstimation = estimatedCalls.find((el, ix, list) => "gasEstimate" in el && (ix === list.length - 1 || "gasEstimate" in list[ix + 1]));
            if (!successfulEstimation) {
                const errorCalls = estimatedCalls.filter((call) => "error" in call);
                if (errorCalls.length > 0)
                    throw errorCalls[errorCalls.length - 1].error;
                throw new Error("Unexpected error. Please contact support: none of the calls threw an error");
            }
            const { call: { contract, parameters: { methodName, args, value }, }, gasEstimate, } = successfulEstimation;
            return contract[methodName](...args, Object.assign({ gasLimit: (0, utils_1.calculateGasMargin)(gasEstimate) }, (value && !(0, utils_1.isZero)(value) ? { value, from: recipient } : { from: recipient })));
        });
    }
    cancelOrder(order) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            // To-Do
            const tx = yield this._autonomyRegistry.cancelHashedReq(order.id, [
                order.requester,
                order.target,
                order.referer,
                order.callData,
                order.initEthSent,
                order.ethForCall,
                order.verifySender,
                order.insertFeeAmount,
                order.payWithAuto,
            ]);
            return tx;
        });
    }
    getOrders(requester) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const { data } = yield this._api.get(`/?chainId=${this._chainId}&address=${requester.toLowerCase()}`);
            const orders = JSON.parse(data);
            return orders.filter((order) => order.callData.includes(this._routerAddress.toLowerCase().substr(2)));
        });
    }
    getOpenOrders(requester) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const orders = yield this.getOrders(requester);
            return orders.filter((order) => order.status === "open");
        });
    }
    getExecutedOrders(requester) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const orders = yield this.getOrders(requester);
            return orders.filter((order) => order.status === "executed");
        });
    }
    getCancelledOrders(requester) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const orders = yield this.getOrders(requester);
            return orders.filter((order) => order.status === "cancelled");
        });
    }
    getOrdersByType(requester, orderType) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const orders = yield this.getOrders(requester);
            return orders.filter((order) => order.type === orderType);
        });
    }
    getMultipleContractSingleData(addresses, contractInterface, methodName) {
        return (0, tslib_1.__awaiter)(this, void 0, void 0, function* () {
            const fragment = contractInterface.getFunction(methodName);
            const callData = contractInterface.encodeFunctionData(fragment, undefined);
            try {
                const results = yield this._multicall.callStatic.tryBlockAndAggregate(false, addresses.map((address) => ({ target: address, callData })));
                return results.returnData.map(({ success, returnData }) => {
                    if (success && returnData) {
                        try {
                            return contractInterface.decodeFunctionResult(fragment, returnData);
                        }
                        catch (err) {
                            console.debug("Result data parsing failed", fragment, returnData);
                            return undefined;
                        }
                    }
                    return undefined;
                });
            }
            catch (err) {
                console.debug("Failed to fetch chunk", err);
                throw err;
            }
        });
    }
    getTokenContract(tokenAddress) {
        const tokenContract = this._signer
            ? new ethers_1.Contract(tokenAddress, ERC20_json_1.default, this._signer)
            : this._provider
                ? new ethers_1.Contract(tokenAddress, ERC20_json_1.default, this._provider)
                : new ethers_1.Contract(tokenAddress, ERC20_json_1.default);
        return tokenContract;
    }
    calculatePairAddress({ tokenA, tokenB }) {
        const tokens = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA];
        return (0, address_1.getCreate2Address)(this._factoryAddress, (0, solidity_1.keccak256)(["bytes"], [(0, solidity_1.pack)(["address", "address"], [tokens[0].address, tokens[1].address])]), this._initCodeHash);
    }
    getSwapCall(inputToken, outputToken, inputAmount, outputAmount, options) {
        const nativeIn = inputToken === constants_2.NATIVE_WRAPPED_TOKEN_ADDRESS[this._chainId];
        const nativeOut = outputToken === constants_2.NATIVE_WRAPPED_TOKEN_ADDRESS[this._chainId];
        const nativeSymbol = constants_2.NATIVE_TOKEN_TICKER[this._chainId];
        const amountIn = inputAmount.toHexString();
        const amountOut = outputAmount.toHexString();
        const { feeOnTransfer, recipient, path, deadline } = options;
        let methodName;
        let args;
        let value;
        if (nativeIn) {
            methodName = feeOnTransfer
                ? `swapExact${nativeSymbol}ForTokensSupportingFeeOnTransferToken`
                : `swapExact${nativeSymbol}ForTokens`;
            args = [amountOut, path, recipient, deadline];
            value = amountIn;
        }
        else if (nativeOut) {
            methodName = feeOnTransfer
                ? `swapExactTokensFor${nativeSymbol}SupportingFeeOnTransferTokens`
                : `swapExactTokensFor${nativeSymbol}`;
            args = [amountIn, amountOut, path, recipient, deadline];
            value = "0x00";
        }
        else {
            methodName = feeOnTransfer ? "swapExactTokensForTokensSupportingFeeOnTransferTokens" : "swapExactTokensForTokens";
            args = [amountIn, amountOut, path, recipient, deadline];
            value = "0x00";
        }
        return { parameters: { methodName, args, value }, contract: this._router };
    }
    encodeSwapCall(orderType, inputToken, outputToken, inputAmount, outputAmount, options) {
        const swapCalls = [];
        swapCalls.push(this.getSwapCall(inputToken, outputToken, inputAmount, outputAmount, Object.assign({ feeOnTransfer: false }, options)));
        swapCalls.push(this.getSwapCall(inputToken, outputToken, inputAmount, outputAmount, Object.assign({ feeOnTransfer: true }, options)));
        const { prepay, recipient } = options;
        return swapCalls.map(({ parameters: { methodName, args, value }, contract }) => {
            const params = [contract.address, ...args];
            let calldata = "0x00";
            let ethForCall = "0x00";
            let swapMethod;
            let swapArgs;
            let verifySender = true;
            let insertFeeAmount = false;
            switch (methodName) {
                case "swapExactETHForTokens":
                case "swapETHForExactTokens":
                case "swapExactETHForTokensSupportingFeeOnTransferTokens":
                case "swapExactAVAXForTokens":
                case "swapAVAXForExactTokens":
                case "swapExactAVAXForTokensSupportingFeeOnTransferTokens":
                    swapMethod = "ethToTokenRange";
                    swapArgs = [
                        ethers_1.BigNumber.from("9999999999999999"),
                        params[0],
                        outputAmount,
                        ethers_1.BigNumber.from("115792089237316195423570985008687907853269984665640564039457584007913129639935"),
                        params[2],
                        params[3],
                        params[4],
                    ];
                    if (!prepay) {
                        swapMethod = `${swapMethod}PayDefault`;
                        swapArgs = [
                            params[3],
                            "0x00",
                            ethers_1.BigNumber.from("9999999999999999"),
                            params[0],
                            outputAmount,
                            ethers_1.BigNumber.from("115792089237316195423570985008687907853269984665640564039457584007913129639935").div(ethers_1.BigNumber.from(10).mul(ethers_1.BigNumber.from(inputAmount))),
                            params[2],
                            params[4],
                        ];
                        insertFeeAmount = true;
                    }
                    else {
                        verifySender = false;
                    }
                    if (orderType === "Stop") {
                        if (!prepay) {
                            swapArgs = [
                                params[3],
                                "0x00",
                                ethers_1.BigNumber.from("9999999999999999"),
                                params[0],
                                outputAmount.mul(85).div(100),
                                outputAmount,
                                params[2],
                                params[4],
                            ];
                        }
                        else {
                            swapArgs = [
                                ethers_1.BigNumber.from("9999999999999999"),
                                params[0],
                                outputAmount.mul(85).div(100),
                                outputAmount,
                                params[2],
                                params[3],
                                params[4],
                            ];
                        }
                    }
                    calldata = this._autonomyMidRouter.interface.encodeFunctionData(swapMethod, swapArgs);
                    ethForCall = value;
                    break;
                case "swapExactTokensForETH":
                case "swapTokensForExactETH":
                case "swapExactTokensForETHSupportingFeeOnTransferTokens":
                case "swapExactTokensForAVAX":
                case "swapTokensForExactAVAX":
                case "swapExactTokensForAVAXSupportingFeeOnTransferTokens":
                    swapMethod = "tokenToEthRange";
                    swapArgs = [
                        recipient,
                        ethers_1.BigNumber.from("9999999999999999"),
                        params[0],
                        inputAmount,
                        outputAmount,
                        ethers_1.BigNumber.from("115792089237316195423570985008687907853269984665640564039457584007913129639935"),
                        params[3],
                        params[4],
                        params[5],
                    ];
                    if (!prepay) {
                        swapMethod = `${swapMethod}PayDefault`;
                        swapArgs = [
                            recipient,
                            "0x00",
                            ethers_1.BigNumber.from("9999999999999999"),
                            params[0],
                            inputAmount,
                            outputAmount,
                            ethers_1.BigNumber.from("115792089237316195423570985008687907853269984665640564039457584007913129639935").div(ethers_1.BigNumber.from(10).mul(ethers_1.BigNumber.from(inputAmount))),
                            params[3],
                            params[5],
                        ];
                        insertFeeAmount = true;
                    }
                    if (orderType === "Stop") {
                        if (!prepay) {
                            swapArgs = [
                                recipient,
                                "0x00",
                                ethers_1.BigNumber.from("9999999999999999"),
                                params[0],
                                inputAmount,
                                outputAmount.mul(85).div(100),
                                outputAmount,
                                params[3],
                                params[5],
                            ];
                        }
                        else {
                            swapArgs = [
                                recipient,
                                ethers_1.BigNumber.from("9999999999999999"),
                                params[0],
                                inputAmount,
                                outputAmount.mul(85).div(100),
                                outputAmount,
                                params[3],
                                params[4],
                                params[5],
                            ];
                        }
                    }
                    calldata = this._autonomyMidRouter.interface.encodeFunctionData(swapMethod, swapArgs);
                    break;
                case "swapExactTokensForTokens":
                case "swapTokensForExactTokens":
                case "swapExactTokensForTokensSupportingFeeOnTransferTokens":
                    swapMethod = "tokenToTokenRange";
                    swapArgs = [
                        recipient,
                        ethers_1.BigNumber.from("9999999999999999"),
                        params[0],
                        inputAmount,
                        outputAmount,
                        ethers_1.BigNumber.from("115792089237316195423570985008687907853269984665640564039457584007913129639935"),
                        params[3],
                        params[4],
                        params[5],
                    ];
                    if (!prepay) {
                        swapMethod = `${swapMethod}PayDefault`;
                        swapArgs = [
                            recipient,
                            "0x00",
                            ethers_1.BigNumber.from("9999999999999999"),
                            params[0],
                            inputAmount,
                            outputAmount,
                            ethers_1.BigNumber.from("115792089237316195423570985008687907853269984665640564039457584007913129639935").div(ethers_1.BigNumber.from(10).mul(ethers_1.BigNumber.from(inputAmount))),
                            params[3],
                            params[5],
                        ];
                        insertFeeAmount = true;
                    }
                    if (orderType === "Stop") {
                        if (!prepay) {
                            swapArgs = [
                                recipient,
                                "0x00",
                                ethers_1.BigNumber.from("9999999999999999"),
                                params[0],
                                inputAmount,
                                outputAmount.mul(85).div(100),
                                outputAmount,
                                params[3],
                                params[5],
                            ];
                        }
                        else {
                            swapArgs = [
                                recipient,
                                ethers_1.BigNumber.from("9999999999999999"),
                                params[0],
                                inputAmount,
                                outputAmount.mul(85).div(100),
                                outputAmount,
                                params[3],
                                params[4],
                                params[5],
                            ];
                        }
                    }
                    calldata = this._autonomyMidRouter.interface.encodeFunctionData(swapMethod, swapArgs);
                    break;
            }
            const wrapperArgs = [
                this._autonomyMidRouter.address,
                "0x0000000000000000000000000000000000000001",
                calldata,
                ethers_1.BigNumber.from(ethForCall),
                verifySender,
                insertFeeAmount,
                false,
            ];
            // const wrapperCalldata = registryContract.interface.encodeFunctionData('newReq', wrapperArgs)
            // Cap original value with autonomy fee - 0.01 ether
            const wrapperValue = prepay
                ? ethers_1.BigNumber.from(value).add(ethers_1.ethers.utils.parseEther("0.01")).toHexString()
                : ethers_1.BigNumber.from(value).toHexString();
            return {
                parameters: {
                    methodName: "newReq",
                    args: wrapperArgs,
                    value: wrapperValue,
                },
                contract: this._autonomyRegistry,
            };
        });
    }
}
exports.AutonomyLimitStopOrders = AutonomyLimitStopOrders;
